import PropTypes from "prop-types";
import { useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import { HoverChild, HoverParent } from "UI/HoverChildren";

import RecurringGiftItem from "./RecurringGiftItem";
import determineHideAllEditButtons from "./determineHideAllEditButtons";

InactiveRecurringGiftsSection.propTypes = {
  recurringGifts: PropTypes.array,
};

export default function InactiveRecurringGiftsSection({ recurringGifts }) {
  const [showInactiveGifts, setShowInactiveGifts] = useState(false);
  const [triggerResize, setTriggerResize] = useState(0);

  const expiredGifts = recurringGifts.filter((g) => g.status === "expired");

  const toggleShowInactiveGifts = () =>
    setShowInactiveGifts((currentVal) => !currentVal);

  const hideAllEditButtons = determineHideAllEditButtons([...expiredGifts]);

  if (!expiredGifts?.length) return null;

  return (
    <HoverParent
      hoverStyle={{
        background: "rgba(0,0,0,.1)",
        borderRadius: 18,
      }}
      triggerResize={triggerResize}
    >
      <ButtonWrapper>
        <HoverChild>
          <GhostButton
            style={{ fontSize: showInactiveGifts ? "16px" : "12px" }}
          >
            Inactive Recurring Gifts
            <Icon className="fas fa-chevron-down" />
          </GhostButton>
          <ActualButton
            whileTap={{ scale: 0.9 }}
            onClick={toggleShowInactiveGifts}
            initial={{ fontSize: "12px" }}
            animate={{ fontSize: showInactiveGifts ? "16px" : "12px" }}
            onAnimationStart={() =>
              setTriggerResize((currentVal) => currentVal + 1)
            }
          >
            Inactive Recurring Gifts
            <Icon
              className="fas fa-chevron-down"
              initial={{ y: 1 }}
              animate={{
                rotate: showInactiveGifts ? -180 : 0,
                y: showInactiveGifts ? 0 : 1,
              }}
            />
          </ActualButton>
        </HoverChild>
      </ButtonWrapper>
      <AnimatePresence>
        {showInactiveGifts && (
          <InactiveGiftsWrapper
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
          >
            <Border />
            {Boolean(expiredGifts?.length) && (
              <>
                {expiredGifts.map((gift, i) => (
                  <RecurringGiftItem
                    key={gift.ID}
                    isLastItem={i >= expiredGifts.length - 1}
                    hideAllEditButtons={hideAllEditButtons}
                    {...gift}
                  />
                ))}
              </>
            )}
          </InactiveGiftsWrapper>
        )}
      </AnimatePresence>
    </HoverParent>
  );
}

const ButtonWrapper = styled.div`
  margin-left: 6px;
  width: fit-content;
  min-height: 34px;
  position: relative;
`;

const GhostButton = styled.div`
  font-size: 12px;
  font-weight: 400;
  padding: 6px 10px;

  cursor: pointer;
  z-index: 100;
  position: relative;
  display: flex;
  align-items: center;
  opacity: 0;
  white-space: nowrap;
`;

const Icon = styled(motion.div)`
  margin-left: 4px;
`;

const ActualButton = styled(motion(GhostButton))`
  opacity: 1;
  position: absolute;
  top: 0;
`;

const InactiveGiftsWrapper = styled(motion.div)`
  overflow: hidden;
  margin-left: 16px;
`;

const Border = styled.div`
  height: 4px;
  border-bottom: 1px solid #d4d9db;
`;
